<template>
  <div class="MapLocationSelection d-flex flex-column">
    <b-aspect
      id="mapbox"
      :aspect="mapAspect"
    >
      <!-- <Button
        v-if="!locationPickerDisabled && !alternative"
        id="evmaps-link"
        class="align-self-end p-button-sm grey"
        @click="navigateToMaps"
      >
        EVMaps
        <i class="fas fa-external-link-alt ml-2" />
        <b-tooltip
          target="evmaps-link"
          placement="right"
        >
          {{ $t('processes.map.goTo', { target: 'EV Maps' }) }}
        </b-tooltip>
      </Button> -->
      <MapBox
        :class="['RealisationDetail__map', { disabled: !getActiveMunicipality && !alternative }, { 'new-process': isNewProcess }]"
        :access-token="accessToken"
        :map-style.sync="mapStyle"
        :options="mapOptions"
        @load="onMapLoaded"
        @moved="fetchRecordsBoundingBox"
      >
        <LayerChargingpoints
          v-if="showLayer"
          :key="`Chargingpoints-${componentKey}`"
          :municipality-code="getActiveMunicipality"
          :show-map-items="showMapItems"
        />
        <div>
          <LayerRequests
            :key="`Requests-${requestComponentKey}`"
            :realisation-uuid="record.uuid"
            :municipality-code="getActiveMunicipality"
            :show-map-items="showMapItems"
            :show-layer="showLayer"
            @click="handleOpenRequestPopup"
          />
          <LayerRealisationProcesses
            v-if="!isLoading"
            :key="`RealisationProcesses-${componentKey}`"
            :active-uuid="record.uuid"
            :show-layer="showLayer"
            :municipality-code="getActiveMunicipality"
            :show-map-items="showMapItems"
            @click="handleOpenRealisationPopup"
          />
        </div>

        <LayerLocationPicker
          v-if="isNewProcess || (originalCoordinates || coordinates)"
          :use-geocoder="useGeocoder"
          :coordinates="originalCoordinates || coordinates"
          :locked="alternative || locked"
          :disabled="alternative || locationPickerDisabled || !canChangeLocation"
          @location="handleLocationPicker"
        />

        <PopupRequest
          :key="`PopupRequest-${popupComponentKey}`"
          :record="record"
          :uuid="requestUuid"
          @close="handleOpenRequestPopup"
        />

        <PopupChargingLocation
          :key="`PopupChargingpoint-${popupComponentKey}`"
          :record="record"
          :locked="(!locationEditEnabled && locked) && !alternative"
          :selected="selected"
          :alternative="alternative"
          @location="handlePreValidatedLocation"
        />
      </MapBox>
      <b-tooltip
        v-if="isNewProcess && !getActiveMunicipality && !alternative"
        target="mapbox"
        triggers="hover"
      >
        {{ $t('Information.selectMunicipality') }}
      </b-tooltip>
      <div
        v-if="isLoading"
        :class="['mapbox-loader', { isLoading }]"
      >
        <b-spinner
          variant="secondary"
          style="width: 3rem; height: 3rem"
        />
      </div>
    </b-aspect>
    <div class="d-flex justify-content-between align-items-start position-relative">
      <MapFilterItems
        v-if="showLayer"
        :municipality-code="getActiveMunicipality"
        :show-map-items="showMapItems"
        :alternative="alternative"
        @panelOpened="popupComponentKey++"
        @toggleMapLocations="$event => showMapItems = $event"
      />
      <Button
        v-else
        type="button"
        :label="$t('processes.map.legend')"
        class="align-self-end p-button-sm mb-3"
        :disabled="true"
      />

      <MapLocationControls
        v-if="canModifyChargingLocation"
        :is-pinned-location="!isLocationControlsDisabled && isPinnedLocation"
        :has-pinned-address="!isLocationControlsDisabled && hasPinnedAddress"
        :disabled="disabled"
        :is-asset-process="!!assetProcessType"
        :location-edit-enabled="locationEditEnabled || (!locked && !isCorrection)"
        :alternative="alternative"
        :is-new-process="isNewProcess"
        :selected="selected"
        :municipality-code="municipalityCode"
        :geocoder-address="geocoderAddress"
        :connection-enabled="connectionEnabled"
        v-on="$listeners"
        @useGeocoderAddress="handleUseGeocoderAddress"
        @resetLocation="handleResetLocation"
        @disconnectLocation="handlePreValidatedLocation"
        @cancelLocationChange="handleCancelLocationChange"
      />
    </div>
    <b-alert
      v-if="zoomAlert"
      show
      variant="info"
    >
      {{ $t('processes.zoomAlert') }}
    </b-alert>

    <div class="AddressWrapper d-flex">
      <div class="AddressWrapper__data w-100">
        <!-- <div class="d-flex flex-column">
          <strong v-if="showAddress">{{ $t('processes.fields.coordinates') }} <span v-if="!!assetProcessType"> {{ $t('processes.fields.new_charger') }}</span></strong>
        </div> -->
        <div>
          <Form
            ref="mapLocationForm"
            @error="handleValidationError"
            @submit="handleSubmit"
          >
            <template v-if="formSteps">
              <div class="DetailGrid__map">
                <DynamicForm
                  :key="formKey"
                  :form="formSteps"
                  :values="addressValues"
                  :uuid="uuid === 'new' ? null : uuid"
                  :disabled="isAddressDisabled"
                  :correction="correction"
                  @input="handleUpdate"
                />
              </div>
            </template>
          </Form>
        </div>
      </div>
    </div>
    <div class="toast-wrapper">
      <b-toast
        id="b-toast"
        :title="$t('warning')"
        variant="warning"
        solid
      >
        {{ bToastMessage }}
      </b-toast>
    </div>
  </div>
</template>

<script>
import MapBox from '@/components/common/MapBox';
import LayerChargingpoints from '@/components/map/LayerChargingpoints';
import LayerRequests from '@/components/map/LayerRequests';
import LayerLocationPicker from '@/components/map/LayerLocationPicker';
import PopupRequest from '@/components/map/PopupRequest';
import MapLocationControls from '@/components/map/MapLocationControls';
import MapFilterItems from '@/components/map/MapFilterItems';

import Button from 'primevue/button'
import { setValidations } from '@/services/dynamicFormHelpers'

import LayerRealisationProcesses from '@/components/map/LayerRealisationProcesses';
//import PopupRealisationProcess from '@/components/map/PopupRealisationProcess';
import PopupChargingLocation from '@/components/map/PopupChargingLocation.vue';
import { getCoordinatesFromAddress, getPlaceNamesFromCoords, getMapboxLanguages, getMapboxPlaceNames } from '@/services/geocoder';
import { fetchProcessesByType } from '@/services/processes'

import { formatPostalCode, normalizePlaceName } from '@/helpers/string';
//import { turf } from '@/services/turf'
import Form from '@/components/form/Form';
import DynamicForm from '@/views/processes/default/DynamicForm.vue';
//import Address from '@/components/definitions/Address';
import { mapGetters, mapActions } from 'vuex';
import CustomBaseStep from '@/views/processes/CustomBaseStep.vue';

import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

export default {
  name: 'CustomMapLocation',
  components: {
    MapBox,
    LayerRequests,
    LayerChargingpoints,
    LayerLocationPicker,
    PopupRequest,
   // Address,
    PopupChargingLocation,
    MapLocationControls,
    LayerRealisationProcesses,
    MapFilterItems,
    //PopupRealisationProcess,
    Button,
    DynamicForm,
    Form
  },
  mixins: [CustomBaseStep],
  props: {
    // record: {
    //   type: Object,
    //   required: true
    // },
    // values: {
    //   type: Object,
    //   required: true
    // },
    // locked: {
    //   type: Boolean,
    //   default: false
    // },
    // disabled: {
    //   type: Boolean,
    //   default: false
    // },
    municipalityCode: {
      type: String,
      default: ''
    },
    alternative: {
      type: Boolean,
      default: false
    },
    // correction: {
    //   type: Boolean,
    //   default: false
    // },
    locationEditEnabled: {
      type: Boolean,
      default: false
    },
    connect: {
      type: Boolean,
      default: true
    },
    cancelChange: {
      type: Boolean,
      default: false
    },
    assetProcessType: {
      type: Object,
      default: null
    },
    // realisationCategory: {
    //   type: String,
    //   default: ''
    // },
    // realisationLocationData: {
    //   type: Object,
    //   default: null
    // },
    canChangeLocation: {
      type: Boolean,
      default: true
    },
    connectionEnabled: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      map: null,
      mapbox: null,
      loaded: false,
      accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
      mapStyle: process.env.VUE_APP_MAPBOX_STYLE,
      geocoder: null,
      revGeocoderComponents: null,
      revGeocoderCoordinates: null,
      addressValues: null,
      addressComponentKey: 0,
      requestUuid: null,
      toggleValue: false,
      popupRealisationUuid: null,
      componentKey: 0,
      requestComponentKey: 0,
      center: [],
      isPinnedLocation: false,
      linkedLocation: null,
      googleMunicipalityCode: '',
      coordinates: null,
      useGeocoder: true,
      manualCoordinatesInput: false,
      originalCoordinates: null,
      recordValues: null,
      isLoading: false,
      popupComponentKey: 0,
      isLocationControlsDisabled: true,
      lastGeocoderResult: null,
      showMapItems: true,
      isMapCollapseOpen: false,
      bToastMessage: null,
      selectedValues: null,
      showLayer: true,
      zoomAlert: false,
      formKey: 0
    };
  },
  computed: {
    //...mapGetters('realisations', ['records']),
    ...mapGetters('requests', ['getUpdatedRequestCounter']),
    ...mapGetters('chargingpoints', ['getChargingPointByUUID', 'getActiveMunicipality', 'chargingpoints', 'getInitialChargingpoints', 'getCount', 'getSelectedFilterValues']),
    ...mapGetters('realisation', ['getSavedRecordValues', 'getActiveRecordValues']),
    ...mapGetters('tenant', ['getMunicipalityOptions', 'getAllMunicipalityOptions', 'getMapOptions', 'getUseAdditionalCoordinates', 'getActiveProcessTypes', 'getBaseUrl', 'getSteps']),
    ...mapGetters('processes', ['getSelectedProcess', 'getSelectedAsset', 'getCurrentProcessType', 'getRecordLocked']),
    ...mapGetters('user', ['canChangeProcessLocation']),

    formSteps () {
      const steps = this.getSteps?.find(step => step.form?.some(f => f.panel === 'map'))?.form?.filter(f => f.panel === 'map') || []
      // if (steps.length) {
      //   Object.values(steps.at(0).fields).map(field => field.disabled = true)
      // }
      return steps
    },
    requests() {
      return [];
    },

    isDevelopment () {
      return ['-dev', '-demo', 'localhost', '-preview'].some(type => this.getBaseUrl?.includes(type))
    },

    realisationProcessData () {
      return this.getSelectedProcess?.realisationProcessData
    },

    mapOptions() {
      if (this.requests.length) {
        return {};
      } else {
        let center = this.getMapOptions.center || [4.9041, 52.3676];
        let focus = false;

        if (this.coordinates) {
          focus = true;
          center = [this.coordinates.lng, this.coordinates.lat];
        }

        return {
          center,
          zoom: focus || this.municipalityCode ? 15 : this.getMapOptions.zoom || 11
        };
      }
    },

    singleMunicipality () {
      return this.municipalityOptions?.length === 1
    },

    isCompleted () {
      return this.record.status.completed
    },

    canModifyChargingLocation () {
      if (this.isCompleted) return false;
      return this.canChangeProcessLocation && (!this.locked && this.canChangeLocation && this.showAddress && !this.assetProcessType)
    },

    showAddress () {
      return !this.assetProcessType || ['realisation', 'reposition', 'expansion'].includes(this.record.processType?.value)
    },

    isAddressDisabled() {
      if (!this.canChangeProcessLocation || !this.isAllowedToCreateLocation) return true;
      return (!this.isNewProcess && (this.getRecordLocked || this.locked)) || this.disabled
    },

    isVlaanderen() {
      return ['vlaanderen', 'mechelen', 'engie'].includes(process.env.VUE_APP_TENANT);
    },

    fallBackCityName () {
      // Fallback for city if geocoder returns none //
      return this.municipalityOptions.find(option => option.value === this.municipalityCode)?.text || ''
    },

    geocoderAddress() {
      if (this.revGeocoderComponents === null) return '';
      if (this.revGeocoderComponents === false || !this.revGeocoderComponents?.street) {
        return this.$i18n.t('Process.geocoderNoLocation');
      }

      if (this.selected || !this.revGeocoderComponents) return '';

      const address = this.revGeocoderComponents;
      // Bilingual (Brussels) result: street/city are Dutch, show the French variant in a French UI //
      const isFrench = this.$i18n.locale === 'fr';
      const street = (isFrench && address.streetFr) || address.street;
      const city = (isFrench && address.cityFr) || address.city || this.fallBackCityName;
      return `${street}${address.street_number ? ' ' + address.street_number : ''}, ${formatPostalCode(
        address.postalCode
      )} ${city}`;
    },

    hasPinnedAddress() {
      const { address } = this.addressValues.location
      return (
        this.manualCoordinatesInput ||
        (!!this.geocoderAddress &&
          !!this.revGeocoderComponents &&
          address.latitude !== this.coordinates?.lat &&
          address.longitude !== this.coordinates?.lng)
      );
    },

    selected() {
      const chargingStation = this.addressValues?.location?.chargingStation
      return chargingStation && this.coordinates ? this.addressValues.location.chargingStation.chargingpointUuid : '';
    },

    locationPickerDisabled() {
      //console.log(!!this.assetProcessType, this.disabled, this.isAllowedToCreateLocation)
      return !!this.assetProcessType || this.disabled || !this.isAllowedToCreateLocation;
    },
    isAllowedToCreateLocation() {
      if (!this.isRealisationProcess) {
        return true
      }

      return this.singleMunicipality ||
             this.municipalityOptions.some(municipality => {
               return municipality.value === (this.municipalityCode || this.values.Municipality) && municipality.locationSelectionAllowed
             })
    },
    hasCpoAdvice() {
      return this.addressValues.BrokerApproval?.IsBrokerAccepted || this.recordValues?.CPOAdvice?.CPOAdvice;
    },
    isConnectLocationDisabled() {
      return this.alternative || this.isCorrection || !this.locationEditEnabled;
    },
    isCorrection() {
      return this.correction && !!this.hasCpoAdvice;
    },
    isNewProcess () {
      return this.$route.name.toLowerCase().startsWith('new')
    },
    mapAspect () {
      return this.isNewProcess ? '16:6' : this.alternative ? '16:5' : '16:8'
    },
    isRealisationPrivate () {
      return this.realisationCategory === 'private'
    },
    isWallonia () {
      return this.getCurrentProcessType === 'wallonia'
    },
    isProcessManualOrPrivate () {
      const isManualProcess =
        ['manual', 'import']
          .some(type => type === this.realisationProcessData?.processType?.value)
      return isManualProcess
    },
    municipalityOptions () {
      const privateOrManualProlcess = this.isProcessManualOrPrivate || this.isRealisationPrivate
      return privateOrManualProlcess ? this.getAllMunicipalityOptions : this.getMunicipalityOptions
    },
    isRealisationProcess () {
      return ['realisation', 'brussels', 'wallonia', 'takeover'].includes(this.getCurrentProcessType)
    },
    currentMunicipalityCode () {
      return this.record?.MunicipalityCode
    },
    hasSelectedFilterValues () {
      return Object.values(this.getSelectedFilterValues).some(val => typeof val !== 'string' && val?.length)
    }
  },
  watch: {
    '$i18n.locale': {
      handler () {
        this.formKey++
        if (this.geocoder) {
          this.map.removeControl(this.geocoder);
          this.$nextTick(() => this.addSearchBox())
        }
      }
    },
    record: {
      immediate: true,
      deep: true,
      handler () {
        // Reset geocoder stuff
        if (!this.isNewProcess) {
          this.revGeocoderComponents = null;
          this.revGeocoderCoordinates = null;
        }

        this.addressValues = this.record.CurrentLocation;

        const { latitude, longitude } = this.record.CurrentLocation?.location?.address || {}
        if (latitude && longitude) {
          this.coordinates = {
            lat: latitude,
            lng: longitude
          }

          this.selectedValues = this.addressValues
          this.showMapItems = !this.isNewProcess

          setTimeout(() => {
            this.flyToLocation({ center: [longitude, latitude], duration: 500 });
            // Load map items after "flyTo" finishes //
            this.selectedValues = null
            this.showMapItems = true
          }, 500)
        }
      }
    },
    /**
     * Be on the look out for address changes triggered from the parent component
     */
    values() {
      this.addressValues = this.values;
      // if (!this.isNewProcess) {
      //   this.addressValues = this.values;
      // }
    },
    getUpdatedRequestCounter: {
      handler () {
        // Update component if connected requests are updated //
        this.$nextTick(() => {
          this.requestComponentKey++;
        })
      }
    },
    locked: {
      handler(locked) {
        if (locked) {
          this.formKey++
        }
      }
    },
    revGeocoderComponents: {
      async handler(components) {
        if (components) {
          // Geocode result in Vlaanderen doesn't have municipality value, only city //
          const city = components.municipality || components.city || this.fallBackCityName
          const matchesOption = names => this.municipalityOptions.some(o => names.includes(normalizePlaceName(o.text)))

          if (matchesOption([normalizePlaceName(city)])) {
            this.isLocationControlsDisabled = false
            return
          }

          // Google returns a single name (e.g. "Amel" for Amblève), try all official language variants //
          const coordinates = this.revGeocoderCoordinates
          const { name, names } = coordinates ? await getPlaceNamesFromCoords({ lng: coordinates[0], lat: coordinates[1], locale: this.$i18n.locale }) : { name: '', names: [] }
          // Pin may have moved meanwhile //
          if (components !== this.revGeocoderComponents) return

          const isMatch = matchesOption(names.map(normalizePlaceName))
          // Show and save the city in the UI language (e.g. 'Amblève' instead of Google's 'Amel') //
          // Not for bilingual (Brussels) results, those already have city (nl) and cityFr from Google //
          if (isMatch && name && !('cityFr' in components)) this.$set(components, 'city', name)
          this.isLocationControlsDisabled = !isMatch
        }
      }
    },
    municipalityCode: {
      immediate: true,
      async handler(code) {
        if (this.geocoder) {
          this.map.removeControl(this.geocoder);
          this.geocoder = null
        }

        if (this.isNewProcess) {
          this.selectedValues = null
          // Municipality change, reset geocoderAddress //
          const selectedMunicipality = this.municipalityOptions.find(o => o.value === code)
          const isAllowedToCreate = this.isAllowedToCreateLocation && !!selectedMunicipality

          if (this.formSteps?.at(0)) {
            Object.values(this.formSteps.at(0).fields).map(field => field.disabled = !isAllowedToCreate)
            this.formKey++
          }

          this.revGeocoderComponents = null;
          this.revGeocoderCoordinates = null;
          this.coordinates = null;
          if (this.addressValues) {
            this.addressValues.location = { address: {}, chargingStation: {} };
          }

          this.$store.dispatch('chargingpoints/setActiveMunicipality', { code })
          this.$emit('setMunicipality', code)
        }

        if (!this.record.ref || this.map && (!this.coordinates || this.isNewProcess)) {
          this.setMapCenterAndLoadChargingPoints();
        }

        if (this.addressValues?.location?.chargingStation?.chargingpointUuid && !code) {
          this.selectedValues = null
          this.fetchRecordsBoundingBox()
        }

        if (this.map) {
          if (code && !this.geocoder) {
            this.addSearchBox()
          }
        }
      }
    },
    geocoderAddress(address) {
      if (!!address && this.$route.query?.requests && !this.record.ref) {
        this.manualCoordinatesInput = !!address;
      }

    },
    cancelChange: {
      handler(cancel) {
        if (cancel) {
          this.addressValues = this.getSavedRecordValues;

          this.$store.dispatch('realisation/setActiveRecordValues', {
            values: JSON.parse(JSON.stringify(this.getSavedRecordValues))
          });

          this.$nextTick(() => {
           // this.addressComponentKey++;
          });
        }
      }
    },
    // Prevent record save if no location is selected (new process) //
    'addressValues.location': {
      handler (location) {
        if (!location.address) return;
        const hasAddress = Object.values(location.address).length
        if (this.isNewProcess && hasAddress && !['streetAndNumber', 'latitude', 'longitude'].every(field => !!location.address[field])) {
          this.$emit('locationConnected', null);
        }
      }
    },
  },
  created() {
    this.$store.dispatch('chargingpoints/resetRecords');

    setValidations(this.formSteps)

    this.recordValues = JSON.parse(JSON.stringify(this.record.CurrentLocation));

    //this.showMapItems = !this.assetProcessType
    this.addressValues = this.assetProcessType
      ? this.recordValues
      : this.record.ref
        ? JSON.parse(JSON.stringify(this.getActiveRecordValues))
        : this.values;

    // const { Latitude, Longitude } = this.isNewProcess && this.realisationLocationData
    //   ? this.realisationLocationData.Location
    //   : this.addressValues.Location

    // if (Latitude) {
    //   this.coordinates = {
    //     lat: Latitude,
    //     lng: Longitude
    //   };
    // }
    // // If by new asset process the coordinates are set by default //
    // if (this.isNewProcess && !!this.assetProcessType) {
    //   this.getSelectedProcess.CurrentLocation.location.address.latitude = latitude
    //   this.getSelectedProcess.CurrentLocation.Location.Longitude = Longitude
    //   this.$store.dispatch('processes/setSelectedProcess', { process: this.getSelectedProcess })
    // }

    // this.$nextTick(() => {
    //   // const altLocation = this.record.values.Location.CPOAdvice?.Alternative?.Location
    //   // if (this.alternative && (!altLocation || !Object.values(altLocation).length)) {
    //   //   this.coordinates = null;
    //   //   this.addressValues.Location = {};
    //   // }
    // });

    const code = this.municipalityOptions.find(o => o.text === this.addressValues.location?.address?.city)?.value;
    if (code && this.restoreMunicipalityToSelected({ code })) {
      this.googleMunicipalityCode = JSON.parse(JSON.stringify(code));
      this.$emit('setCoordinates', { code });
    }
  },

  beforeDestroy () {
    //this.$store.dispatch('chargingpoints/setActiveMunicipality', { code: '' });
    if (this.geocoder) {
      this.geocoder.off('result', ({ result }) => this.handleGeocoderSearchResult({ result }));
    }
  },

  methods: {
    ...mapActions('chargingpoints', ['loadChargingPointsByCode']),
    ...mapActions('realisations', ['loadRecords']),
    //...mapActions('requests', ['loadRequestsByCode']),

    restoreMunicipalityToSelected({ code }) {
      return (
        this.addressValues.location.city &&
        !this.googleMunicipalityCode &&
        this.municipalityCode &&
        this.municipalityCode !== code
      );
    },

    addSearchBox () {
      const selectedMunicipalityName = normalizePlaceName(this.municipalityOptions.find(o => o.value === this.municipalityCode)?.text)
      this.geocoder = new MapboxGeocoder({
        accessToken: this.accessToken,
        mapboxgl: this.mapbox,
        countries: this.getMapOptions.countries || 'be',
        types: 'address',
        language: getMapboxLanguages(this.$i18n.locale),

        collapsed: !this.isNewProcess,
        placeholder: this.$t('processes.map.search_placeholder'),
        zoom: 15,
        marker: {
          color: '#2196F3',
          scale: 1.5,
          offset: [0, -20]
        },
        filter: (item) => {
          // Contain search autocomplete results only to a selected municipality //
          return item.context.some(i =>
            i.id.startsWith('place') &&
            getMapboxPlaceNames(i).some(name => normalizePlaceName(name) === selectedMunicipalityName)
          );
        }
      });
      this.map.addControl(this.geocoder);

      this.geocoder.on('result', ({ result }) => this.handleGeocoderSearchResult({ result }));
    },

    handleUpdate({ name, value }) {
      const prop = name.split('.').at(-1) || name;
      const numberType = ['latitude', 'longitude'].includes(prop)

      const coordinates = value
        .split(',')
        .map(entry => parseFloat(entry.trim()))
        .filter(Boolean);

      if (coordinates.length > 1) {
        const [latitude, longitude] = coordinates;
        this.addressValues.location.address.latitude = +latitude
        this.addressValues.location.address.longitude = +longitude
        //this.formKey++
        this.handleAddressCoordinates({ longitude, latitude })
      } else {
        this.addressValues.location.address[prop] = numberType ? +value : value
        this.handleAddressCoordinates({ [prop]: value })
      }

      const address = this.addressValues.location.address
      this.coordinates = [address.longitude, address.latitude];
    },

    handleValidationError (event) {
      console.log(event)
    },

    navigateToMaps () {
      // evtools tenant has belgian municipalities as well (e.g. Hasselt ) //
      const isVlaanderen = ['vlaanderen', 'mechelen', 'engie'].includes(process.env.VUE_APP_TENANT) || this.getActiveMunicipality?.length === 5
      const devUrl = isVlaanderen ? 'https://evmaps-be-dev.netlify.app/' : 'https://dev.evmaps.nl'
      const prodUrl = isVlaanderen ? 'https://www.evmaps.be' : 'https://evmaps.nl'

      const url = this.isDevelopment ? devUrl : prodUrl
      window.open(url, '_blank')
    },

    async handleAddressCoordinates() {
      // Update store location address //
      this.getSelectedProcess.CurrentLocation.location.address = this.addressValues.location.address
      this.$store.dispatch('processes/setSelectedProcess', { process: this.getSelectedProcess })
      this.$store.dispatch('realisation/setActiveRecordValues', { values: this.addressValues })

      this.flyToLocation({ center: this.coordinates, duration: 300 });
    },

    handleMapCollapse ({ shown }) {
      this.map.resize();
      this.isMapCollapseOpen = shown
    },

    isSavedRecordCoordinates({ coordinates }) {
      if (!this.record.ref) return;
      const altLocation = this.record.CurrentLocation.CPOAdvice?.Alternative?.Location;
      const { latitude, longitude } = this.alternative && altLocation ? altLocation : this.record.CurrentLocation?.location?.address;

      return latitude === coordinates[1] && longitude === coordinates[0];
    },

    handleGeocoderSearchResult({ result }) {
      this.lastGeocoderResult = result;
      if (!this.locked) {
        this.coordinates = {
          lat: result.center[1],
          lng: result.center[0]
        };
      }
    },

    async fetchRecordsBoundingBox () {
      const zoom = this.map?.getZoom() || 15
      if (this.getActiveMunicipality && zoom <= 13.5) return this.zoomAlert = true
      //if (this.correction) return this.isLoading = false

      this.zoomAlert = false

      if (!this.getActiveMunicipality) return;

      if (!this.isNewProcess && this.selectedValues) {
        this.addressValues = this.selectedValues
        return this.isLoading = false
      }
      this.isLoading = true

      this.showLayer = !this.hasSelectedFilterValues

      const token = await this.$auth.getTokenSilently();
      const bbox = this.map.getBounds()
      const isRealisationPrivate = this.isRealisationPrivate
      const code = this.getActiveMunicipality

      await this.loadChargingPointsByCode({ code, bbox, isRealisationPrivate });
      this.isLoading = false

      await fetchProcessesByType({ code, bbox, token })
      this.componentKey++

      //await this.loadRequestsByCode({ code, bbox, token })

      if (this.hasSelectedFilterValues) {
        this.setFilterMapItems()
      }

      if (this.coordinates) {
        const { lat, lng } = this.coordinates
         // Find the cause of this //
        const location = this.addressValues.location?.location || this.addressValues.location
        this.addressValues.location = location

        this.addressValues.location.address.latitude = lat;
        this.addressValues.location.address.longitude = lng

        this.$nextTick(() => {
          this.addressComponentKey++
        });
      }


      this.$store.dispatch('chargingpoints/setActiveMunicipality', { code })
      this.$emit('setMunicipality', code)

    },

    async setFilterMapItems () {
      this.$store.dispatch('realisations/filterRealisations', { filters: this.getSelectedFilterValues })
      this.$store.dispatch('chargingpoints/filterChargingPoints', { filters: this.getSelectedFilterValues })
      this.$store.dispatch('requests/filterRequests', { filters: this.getSelectedFilterValues, selectedRealisation: this.$route.params.uuid })
      this.$store.dispatch('chargingpoints/loadSelectedFilterValues', { filterValues: this.getSelectedFilterValues })
      this.showLayer = true
    },

    async onMapLoaded({ map, mapbox }) {
      this.loaded = true;
      this.map = map;
      this.mapbox = mapbox;
      if (!this.isNewProcess) {
        this.addSearchBox()
      }

      if (!this.alternative && this.municipalityCode && !this.coordinates) {
        const name = this.municipalityOptions.find(o => o.value === this.municipalityCode)?.text;
        if (name) {
          this.center = await getCoordinatesFromAddress({ address: { city: name }, code: this.municipalityCode });
          this.map.jumpTo({
            center: this.center,
            zoom: 15
          });
        }
      }

      if (this.$route.query?.requests && !this.record.ref) {
        const { lng, lat, code, city } = this.$route.query;
        const existingMunicipality = this.municipalityOptions.some(o => o.value === code);

        if (existingMunicipality) {
          this.$store.dispatch('chargingpoints/setActiveMunicipality', { code })
          this.$emit('setCoordinates', { code });

          setTimeout(() => {
            this.handleAddressCoordinates({ lat, lng });
          }, 1000);
        } else {
          this.$store.dispatch('tenant/setToast', {
            vm: this,
            message: `Behandelende gemeente ${city || ''} is niet gevonden.`,
            error: false,
            variant: 'warning',
            delay: 100
          });
        }
        // Disable record save until location is connected //
        this.$emit('locationConnected', null);
      }
      this.selectedValues = null;
      this.fetchRecordsBoundingBox()
    },

    setLocationAddress ({ location }) {
      const { address } = location
      const streetAndNumberFr = location?.importedData?.['Street & Number (FR)'] ||
        (address.streetFr ? `${address.streetFr} ${address.number || ''}`.trim() : '') ||
        ''
      const cityFr = location?.importedData?.['City (FR)'] || address.cityFr || ''
      const [longitude, latitude] = location.geoPoint.coordinates
      if (address.number && !Number(address.number)) {
        const numberAndSuffix = this.splitNumber(location.address.number);
        address.number = numberAndSuffix[0];
        address.suffix = numberAndSuffix[1];
      }

      const values = {
        brussels: {
          city: address.city,
          streetAndNumber: address.simple_address,
          streetAndNumberFr,
          cityFr,
          postalCode: address.postalcode,
          province: address.province,
          country: address.country,
          latitude,
          longitude
        },
        // Atm. props are the same //
        get wallonia() { return this.brussels }
      }

      this.addressValues.location.address = values[this.getCurrentProcessType]
      this.coordinates = {
        lat: latitude,
        lng: longitude
      }

      this.addressValues.location.chargingStation.chargingpointUuid = location.uuid;
      this.addressValues.location.chargingStation.chargingpointStatus = location.properties.status;
      this.addressValues.location.chargingStation.evtoolsLocationId = `${location.code}-${location.properties.id}`

      this.getSelectedProcess.CurrentLocation.location = this.addressValues.location
      this.$store.dispatch('processes/setSelectedProcess', { process: this.getSelectedProcess })
      //console.log(values[this.getCurrentProcessType], location)
      return values[this.getCurrentProcessType]
    },

    async handlePreValidatedLocation({ uuid }) {
      this.useGeocoder = false;
      this.revGeocoderComponents = null;

      // Deselected = made available again
      if (uuid === null) {
        // If location is unlinked, reset values.Location (free location selection map) //
        this.addressValues.location = { address: {}, chargingStation: {} };
        this.selectedValues = null
        // this.$store.dispatch('realisation/setActiveRecordValues', { values: this.addressValues });

        this.$emit('locationConnected', null);

        this.formKey++;
        this.coordinates = null;
        // this.addressValues.ParkingSpots.Identifiers = [];

        // if (this.alternative) {
        //   const parkinglotIds = this.addressValues.ParkingSpots?.Identifiers?.map(spot => spot.Identifier);
        //   this.$emit('setAlternativeLocation', { altLocation: this.addressValues.Location, parkinglotIds });
        // }

        setTimeout(() => {
          this.useGeocoder = true;
        }, 1000);
        return;
      }

      this.googleMunicipalityCode = JSON.parse(JSON.stringify(this.municipalityCode));
      this.isPinnedLocation = false;

      const location = this.getChargingPointByUUID({ uuid });

      if (location) {
        this.setLocationAddress({ location })

        this.formKey++;

      } else {
        this.addressValues = this.values;
      }

      this.selectedValues = this.addressValues

      this.$emit('locationConnected', this.addressValues);
      this.flyToLocation({ center: location.geoPoint.coordinates, duration: 100 });

      setTimeout(() => {
        this.useGeocoder = true;
      }, 500);
    },

    /**
     * When the user places the location pin
     */
    handleLocationPicker({ coordinates, components }) {
      if (!components) return (this.revGeocoderComponents = components);

      // if (this.record.ref) {
      //   const altLocation = this.record.history[0]?.CPOAdvice?.Alternative?.Location;
      //   const address = this.alternative && altLocation ? altLocation : this.record?.history?.[0]?.location?.address || {};

      //   const { originalAddress, geocoderAddress } = this.formatAddressComponents({ address, components });
      //   console.log(originalAddress, geocoderAddress)
      //   // if (originalAddress !== geocoderAddress) {
      //   //   // this.addressValues.location.address.latitude = coordinates[1]
      //   //   // this.addressValues.location.address.latitude = coordinates[0]
      //   //   this.addressValues.location.address = {
      //   //     latitude: coordinates[1],
      //   //     longitude: coordinates[0]
      //   //   };

      //   //   this.$emit('locationConnected', null);
      //   // } else {
      //   //   this.addressComponentKey++;
      //   // }
      // }

      this.revGeocoderComponents = components;
      this.revGeocoderCoordinates = coordinates;
      this.googleMunicipalityCode = JSON.parse(JSON.stringify(this.municipalityCode));
      //this.handleUseGeocoderAddress()
    },

    formatAddressComponents({ address, components }) {
      const stringify = address => {
        return address.replace(/\s/g, '').trim().toLowerCase();
      };
      return {
        originalAddress: stringify(address.streetAndNumber + address.postalCode + address.city || this.fallBackCityName),
        geocoderAddress: stringify(
          components.street + components.street_number + components.postalCode + components.city || this.fallBackCityName
        )
      };
    },

    handleCancelLocationChange() {
      this.revGeocoderComponents = null;
      this.revGeocoderCoordinates = null;
      if (this.geocoder?.inputString) {
        this.clearGeocoderSearch();
      }
      this.$emit('handleCancelChange', true)

      if (this.isNewProcess) {
        this.addressValues.location.address = {}
        this.$emit('locationConnected', null)
      } else {

        this.fetchRecordsBoundingBox()

        if (this.addressValues.location.address.latitude) {
          this.coordinates = {
            lat: this.addressValues.location.address.latitude,
            lng: this.addressValues.location.address.longitude
          };
          this.flyToLocation({ center: [this.coordinates.lng, this.coordinates.lat], duration: 300 });
        }
      }
    },

    // /**
    //  * When the user changes the coordinates in the Latitude / Longitude input fields
    //  */
    // async handleAddressCoordinates({ lng, lat }) {
    //   if (!!this.assetProcessType && this.realisationProcessData) {
    //     if (!this.realisationProcessData.process.Location.Latitude) return;

    //     const originalLat = this.realisationProcessData.process.Location.Latitude;
    //     const originalLng = this.realisationProcessData.process.Location.Longitude;
    //     const distance = turf.distance([originalLng, originalLat], [lng, lat]);

    //     if (distance * 1000 > 12) {
    //       this.bToastMessage = 'Let op, de coördinaten van de nieuwe laadpaal liggen op meer dan 12 meter van de bestaande laadpaal';
    //       this.$bvToast.show('b-toast')
    //     }
    //   }

    //   this.manualCoordinatesInput =
    //     this.addressValues.location.address.latitude !== lat && this.addressValues.location.address.longitude !== lng; //manualInput

    //   if ((this.isNewProcess && this.isAllowedToCreateLocation) || this.record.ref) {
    //     this.addressValues.location.address.latitude = lat;
    //     this.addressValues.location.address.longitude = lng;
    //     this.addressComponentKey++;
    //   }

    //   const latLng = { lng, lat };
    //   this.coordinates = latLng;

    //   if (this.assetProcessType) {
    //     this.getSelectedProcess.CurrentLocation.location = this.addressValues.location
    //     this.$store.dispatch('processes/setSelectedProcess', { process: this.getSelectedProcess });
    //   }
    //   this.flyToLocation({ center: [lng, lat] });
    // },

    /**
     * Handle a user initiated change in one of the address fields
     */
    handleAddressUpdate({ address }) {
      Object.keys(address).map(key => {
        this.addressValues.location.address[key] = address[key];
      });

      if (this.alternative) {
        this.$emit('setAlternativeLocation', {
          altLocation: this.addressValues.location
        });
      } else {
        this.getActiveRecordValues.location = this.addressValues.location;
        this.$store.dispatch('realisation/setActiveRecordValues', { values: this.getActiveRecordValues });

        if (this.assetProcessType) {
          this.getSelectedProcess.CurrentLocation.location = this.addressValues.location;
          this.$store.dispatch('processes/setSelectedProcess', { process: this.getSelectedProcess });
        }
      }
      this.selectedValues = this.addressValues

      this.$emit('locationConnected', this.addressValues);
    },

    /**
     *
     */
    handleOpenRequestPopup({ uuid }) {
      this.requestUuid = uuid;
    },

    /**
     * https://stackoverflow.com/a/59368102
     */
    splitNumber(str) {
      const [, ...arr] = str.match(/(\d*)([\s\S]*)/);
      return arr;
    },

    async handleUseGeocoderAddress() {
      this.formKey++;
      const address = this.revGeocoderComponents;
      //this.addressValues.ParkingSpots.Identifiers = [];

      const numberAndSuffix = this.splitNumber(address.street_number || '');

      const selectedAddress = {
        street: address.street,
        streetFr: address.streetFr || '',
        number: numberAndSuffix[0] || '',
        streetAndNumber: `${address.street} ${numberAndSuffix[0] || ''}`,
        ...(address.streetFr ? { streetAndNumberFr: `${address.streetFr} ${numberAndSuffix[0] || ''}` } : {}),
        postalCode: formatPostalCode(address.postalCode),
        city: address.city || this.fallBackCityName,
        ...(address.cityFr ? { cityFr: `${address.cityFr}` } : {}),
        province: address.province,
        longitude: this.revGeocoderCoordinates[0],
        latitude: this.revGeocoderCoordinates[1]
      };

      if (this.getUseAdditionalCoordinates) {
        selectedAddress.AdditionalCoordinates = [];
      }

      this.handleAddressUpdate({ address: selectedAddress });
      if (this.geocoder?.inputString) {
        this.clearGeocoderSearch();
      }

      this.googleMunicipalityCode = this.getActiveMunicipality;
      this.$emit('setCoordinates', { code: this.getActiveMunicipality });

      const latLng = {
        lng: this.revGeocoderCoordinates[0],
        lat: this.revGeocoderCoordinates[1]
      };
      this.coordinates = latLng;
      this.manualCoordinatesInput = false;
      this.revGeocoderComponents = null;

      this.flyToLocation({ center: [latLng.lng, latLng.lat], duration: 100 });

    },

    handleResetLocation() {
      this.$emit('setCoordinates', { code: this.googleMunicipalityCode });
      this.isPinnedLocation = false;

      const center = [
        this.addressValues.location.address.longitude || this.coordinates?.lng,
        this.addressValues.location.address.latitude || this.coordinates?.lat
      ].filter(Boolean);

      center.length && this.flyToLocation({ center, duration: 300 });
    },

    handleOpenRealisationPopup({ uuid }) {
      this.popupRealisationUuid = uuid;
    },
    handleCloseRealisationProcessPopup() {
      this.popupRealisationUuid = null;
    },

    async setMapCenterAndLoadChargingPoints() {
      if (!this.addressValues) return

      if (this.addressValues) {
        this.isPinnedLocation =
        this.googleMunicipalityCode !== this.addressValues.Municipality && !!this.addressValues.location.address.latitude;
      }

      if (this.municipalityCode === this.googleMunicipalityCode) {
        return this.handleResetLocation();
      }

      const name = this.municipalityOptions.find(o => o.value === this.municipalityCode)?.text;
      if (name && this.map) {
        this.center = await getCoordinatesFromAddress({ address: { city: name }, code: this.municipalityCode });
        this.flyToLocation({ center: this.center, duration: 500 });
      }
    },

    flyToLocation({ center, duration = 300 }) {
      if (!this.map) return;
      this.map.flyTo({
        center,
        duration,
        zoom: 15,
        essential: true
      });
    },

    clearGeocoderSearch() {
      this.geocoder.clear();
      // Couldn't find any geocoder method to do this (close search box, remove focus)
      document.querySelector('.mapboxgl-ctrl-geocoder--input').blur();
    }
  }
};
</script>
<style lang="scss" scoped>
#evmaps-link {
  position: absolute;
  z-index: 3;
  bottom: 0.5em;
  left: 0.5em;
  padding: 0.4em 0.6em;
}
.p-button.grey {
  &:hover, &:enabled:hover, &:enabled:active {
    border-color: #6c757d;
    background: #666;
    box-shadow: none;
  }
  &.p-button-sm {
    background: #6c757d;
    border-color: #6c757d;
  }
}
.new-process {
  ::v-deep .mapboxgl-ctrl-geocoder {
    min-width: 400px;
  }
}
</style>
